'use client';

import React, { useState, useMemo } from 'react';
import { startOfMonth, endOfMonth, startOfWeek, endOfWeek, eachDayOfInterval, isSameMonth, isSameDay, isToday, addMonths, subMonths, format } from 'date-fns';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { cn } from '@/app/components/lib/utils';
import { WEEKDAYS_MINI } from './constants';
export const CalendarMiniPicker = ({
  selectedDate,
  onSelectDate,
  events = [],
  className,
  slotProps
}) => {
  const [currentMonth, setCurrentMonth] = useState(startOfMonth(selectedDate));
  React.useEffect(() => {
    setCurrentMonth(prev => isSameMonth(prev, selectedDate) ? prev : startOfMonth(selectedDate));
  }, [selectedDate]);
  const days = useMemo(() => {
    const monthStart = startOfMonth(currentMonth);
    const monthEnd = endOfMonth(currentMonth);
    const startDate = startOfWeek(monthStart, {
      weekStartsOn: 1
    });
    const endDate = endOfWeek(monthEnd, {
      weekStartsOn: 1
    });
    return eachDayOfInterval({
      start: startDate,
      end: endDate
    });
  }, [currentMonth]);

  // Set of day keys with events for quick lookup
  const eventDaysSet = useMemo(() => {
    const set = new Set();
    events.forEach(ev => {
      const start = typeof ev.start === 'string' ? new Date(ev.start) : ev.start;
      const end = typeof ev.end === 'string' ? new Date(ev.end) : ev.end;
      const daysBetween = eachDayOfInterval({
        start: startOfMonth(start) > start ? startOfMonth(start) : start,
        end: endOfMonth(end) < end ? endOfMonth(end) : end
      });
      daysBetween.forEach(d => set.add(format(d, 'yyyy-MM-dd')));
    });
    return set;
  }, [events]);
  return <div {...slotProps} className={cn('w-full select-none rounded-xl p-4 text-zinc-900 dark:text-zinc-100', className, slotProps?.className)}>
            {/* Mini Header */}
            <div className="mb-3 flex items-center justify-between">
                <h4 className="text-sm font-semibold text-zinc-900 dark:text-zinc-100">{format(currentMonth, 'MMMM yyyy')}</h4>
                <div className="flex items-center gap-1">
                    <button type="button" onClick={() => setCurrentMonth(subMonths(currentMonth, 1))} className="flex size-7 items-center justify-center rounded-lg text-zinc-500 hover:bg-zinc-100 hover:text-zinc-900 dark:text-zinc-400 dark:hover:bg-zinc-800 dark:hover:text-zinc-100 transition-colors" aria-label="Previous month">
                        <ChevronLeft className="size-4 stroke-[2.25px]" />
                    </button>
                    <button type="button" onClick={() => setCurrentMonth(addMonths(currentMonth, 1))} className="flex size-7 items-center justify-center rounded-lg text-zinc-500 hover:bg-zinc-100 hover:text-zinc-900 dark:text-zinc-400 dark:hover:bg-zinc-800 dark:hover:text-zinc-100 transition-colors" aria-label="Next month">
                        <ChevronRight className="size-4 stroke-[2.25px]" />
                    </button>
                </div>
            </div>

            {/* Weekday labels */}
            <div className="grid grid-cols-7 mb-1 text-center">
                {WEEKDAYS_MINI.map((w, idx) => <span key={idx} className="flex size-8 items-center justify-center text-xs font-medium text-zinc-400 dark:text-zinc-500">
                        {w}
                    </span>)}
            </div>

            {/* Days grid */}
            <div className="grid grid-cols-7 gap-y-1">
                {days.map(day => {
        const inCurrentMonth = isSameMonth(day, currentMonth);
        const isSelected = isSameDay(day, selectedDate);
        const isCurrentDay = isToday(day);
        const dayKey = format(day, 'yyyy-MM-dd');
        const hasEvents = eventDaysSet.has(dayKey);
        return <button key={day.toISOString()} type="button" onClick={() => onSelectDate(day)} className={cn('group relative flex size-8 mx-auto items-center justify-center rounded-full text-xs transition-all', !inCurrentMonth && 'text-zinc-300 dark:text-zinc-600', inCurrentMonth && !isSelected && 'text-zinc-700 dark:text-zinc-300 hover:bg-zinc-100 dark:hover:bg-zinc-800', isSelected && 'bg-purple-600 text-white font-semibold shadow-xs hover:bg-purple-700', isCurrentDay && !isSelected && 'border border-purple-500 font-semibold text-purple-600 dark:text-purple-400')}>
                            <span>{format(day, 'd')}</span>
                            {/* Event dot marker */}
                            {hasEvents && <span className={cn('absolute bottom-1 size-1 rounded-full', isSelected ? 'bg-white' : 'bg-purple-600 dark:bg-purple-400')} />}
                        </button>;
      })}
            </div>
        </div>;
};